/* eslint-disable */

/**
 * Default feature definitions for the application
 *
 * This is the single source of truth for all feature flags and their default states.
 * Features are organized alphabetically by category, then by name.
 *
 * @author Matt Kenefick <matt@polymermallard.com>
 * @package Config
 * @project ChalkySticks Web App
 */
export const defaultFeatures: Record<string, IFeatureMetadata> = {
	// Authentication features
	auth: {
		category: 'auth',
		default: true,
		description: 'Core authentication system',
		name: 'auth',
	},
	auth_login: {
		category: 'auth',
		default: true,
		dependencies: ['auth'],
		description: 'User login functionality',
		name: 'auth_login',
	},
	auth_signup: {
		category: 'auth',
		default: true,
		dependencies: ['auth'],
		description: 'User registration functionality',
		name: 'auth_signup',
	},
	auth_social: {
		category: 'auth',
		default: true,
		dependencies: ['auth', 'auth_login'],
		description: 'Social media authentication (Facebook, Google, etc.)',
		name: 'auth_social',
	},

	// General features
	advertisements: {
		category: 'general',
		default: true,
		description: 'Display advertisements throughout the application',
		name: 'advertisements',
	},
	comments: {
		category: 'general',
		default: true,
		description: 'Enable commenting on content',
		name: 'comments',
	},
	reactions: {
		category: 'general',
		default: true,
		description: 'Enable reactions (likes, emojis) on content',
		name: 'reactions',
	},
	wallet: {
		category: 'general',
		default: true,
		description: 'Digital wallet functionality for in-app purchases',
		name: 'wallet',
	},

	// Kit features
	global_pad: {
		category: 'kit',
		default: true,
		description: 'Global Pad application access',
		name: 'global_pad',
	},
	global_trainer: {
		category: 'kit',
		default: true,
		description: 'Global Trainer application access',
		name: 'global_trainer',
	},
	kit_balldetector: {
		category: 'kit',
		default: true,
		description: 'Ball detection AI tool',
		name: 'kit_balldetector',
	},
	kit_drills: {
		category: 'kit',
		default: true,
		description: 'Practice drills and exercises',
		experimental: true,
		name: 'kit_drills',
	},
	kit_events: {
		category: 'kit',
		default: true,
		description: 'Event management and calendar',
		experimental: true,
		name: 'kit_events',
	},
	kit_handicapper: {
		category: 'kit',
		default: true,
		description: 'Handicap calculation tool',
		experimental: true,
		name: 'kit_handicapper',
	},
	kit_killers: {
		category: 'kit',
		default: true,
		description: 'Game variations and killers tracking',
		experimental: true,
		name: 'kit_killers',
	},
	kit_pad: {
		category: 'kit',
		default: true,
		description: 'Digital scorepad for tracking games',
		name: 'kit_pad',
	},
	kit_pad_scanner: {
		category: 'kit',
		default: true,
		dependencies: ['kit_pad'],
		description: 'Scan QR codes to join pad sessions',
		experimental: true,
		name: 'kit_pad_scanner',
	},
	kit_patternracker: {
		category: 'kit',
		default: true,
		description: 'Track and analyze shot patterns',
		experimental: true,
		name: 'kit_patternracker',
	},
	kit_rulebooks: {
		category: 'kit',
		default: true,
		description: 'Access to official rulebooks',
		experimental: true,
		name: 'kit_rulebooks',
	},
	kit_shotclock: {
		category: 'kit',
		default: true,
		description: 'Shot clock timer for games',
		experimental: true,
		name: 'kit_shotclock',
	},
	kit_trainer: {
		category: 'kit',
		default: true,
		description: 'Training and practice assistant',
		experimental: true,
		name: 'kit_trainer',
	},
	kit_trainer_scanner: {
		category: 'kit',
		default: true,
		dependencies: ['kit_trainer'],
		description: 'Scan QR codes for trainer sessions',
		experimental: true,
		name: 'kit_trainer_scanner',
	},

	// Navigation features
	nav_feed: {
		category: 'nav',
		default: true,
		description: 'Feed navigation tab',
		name: 'nav_feed',
	},
	nav_more: {
		category: 'nav',
		default: true,
		description: 'More/extras navigation tab',
		name: 'nav_more',
	},
	nav_places: {
		category: 'nav',
		default: true,
		description: 'Places/venues navigation tab',
		name: 'nav_places',
	},
	nav_tv: {
		category: 'nav',
		default: true,
		description: 'TV/streaming navigation tab',
		name: 'nav_tv',
	},

	// Venue features
	venue_action_call: {
		category: 'venue',
		default: true,
		description: 'Call functionality at venues',
		name: 'venue_action_call',
	},
	venue_action_website: {
		category: 'venue',
		default: true,
		description: 'Website functionality at venues',
		name: 'venue_action_website',
	},
	venue_action_directions: {
		category: 'venue',
		default: true,
		description: 'Directions functionality at venues',
		name: 'venue_action_directions',
	},
	venue_checkin: {
		category: 'venue',
		default: true,
		dependencies: ['auth'],
		description: 'Check-in functionality at venues',
		name: 'venue_checkin',
	},
	venue_comments: {
		category: 'venue',
		default: true,
		description: 'User comments on venue pages',
		name: 'venue_comments',
	},
	venue_description: {
		category: 'venue',
		default: true,
		description: 'Display venue description text',
		name: 'venue_description',
	},
	venue_favoriting: {
		category: 'venue',
		default: true,
		dependencies: ['auth'],
		description: 'Favorite/save venues functionality',
		name: 'venue_favoriting',
	},
	venue_formatteddetails: {
		category: 'venue',
		default: true,
		description: 'Display formatted venue detail information',
		name: 'venue_formatteddetails',
	},
	venue_friendlyfaces: {
		category: 'venue',
		default: true,
		dependencies: ['auth', 'venue_checkin'],
		description: 'See who else is checked in at venues',
		name: 'venue_friendlyfaces',
	},
	venue_friendlyfaces_ugc: {
		category: 'venue',
		default: true,
		dependencies: ['venue_friendlyfaces'],
		description: 'User-generated content in friendly faces (photo uploads)',
		name: 'venue_friendlyfaces_ugc',
	},
	venue_gallery: {
		category: 'venue',
		default: true,
		description: 'Display venue gallery images',
		name: 'venue_gallery',
	},
	venue_hours: {
		category: 'venue',
		default: true,
		description: 'Display venue operating hours',
		name: 'venue_hours',
	},
	venue_map: {
		category: 'venue',
		default: true,
		description: 'Display venue location on a map',
		name: 'venue_map',
	},
	venue_nearby: {
		category: 'venue',
		default: true,
		description: 'Find nearby venues based on location',
		name: 'venue_nearby',
	},
	venue_openclosed: {
		category: 'venue',
		default: true,
		description: 'Display whether the venue is currently open or closed',
		name: 'venue_openclosed',
	},
	venue_playtherack: {
		category: 'venue',
		default: false,
		description: 'Play the rack game feature at venues',
		name: 'venue_playtherack',
	},
	venue_revision: {
		category: 'venue',
		default: true,
		description: 'Venue revision and edit suggestions',
		name: 'venue_revision',
	},
};

/**
 * Override registry for testing and development
 *
 * Set a feature to `true` or `false` to force its state regardless of
 * what the API or defaults say. Set to `null` to use the normal value.
 *
 * Example:
 *   overrideFeatures['venue_map'] = true;   // Force enabled
 *   overrideFeatures['wallet'] = false;      // Force disabled
 *   overrideFeatures['auth'] = null;         // Use normal value
 */
export const overrideFeatures: Record<string, boolean | null> = {
	// auth: true,
	// venue_map: true,
	// venue_favoriting: false,
	// venue_openclosed: false,

	venue_playtherack: false,
};
